// @flow
import * as React from 'react'
import { map, sortBy, sortedUniq } from 'lodash'
import glamorous from 'glamorous'
import Link from 'gatsby-link'
import { css } from 'glamor'
import Img from 'gatsby-image'
import Helmet from 'react-helmet'
import { Wrapper, Grid, Gif, Filter } from 'components'
import { colors, typography, margin, transition, padding } from 'style'

import type { Location, RouterHistory } from 'react-router-dom'
import type { ContentfulSizes } from 'types'

const ImageOverlay = glamorous.div({
  ...transition.slow('opacity', 'padding'),
  ...padding.bottom(4),
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundImage:
    'linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.92) 100%)',
  color: colors.white,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  opacity: 0,
  ':hover': {
    opacity: 1,
    ...padding.bottom(5),
  },
})

const GalleryTitle = glamorous.h2({
  ...typography.title3,
  ...typography.antialiased,
  ...margin.bottom(2),
  fontWeight: 400,
})

const GalleryDetails = glamorous.p({
  ...typography.meta,
  ...typography.antialiased,
  fontWeight: 400,
})

const blur = css({
  '> img[src^="data"]': {
    filter: 'blur(5px)',
    transform: 'scale(1.03)',
  },
})

type ContentfulGallery = {
  id: string | number,
  title: string,
  slug: string,
  category: {
    title: string,
  },
  featuredImage: {
    title: string,
    sizes: ContentfulSizes,
    file: {
      url: string,
      contentType: string,
    },
    fields: {
      aspectRatio: number,
    },
  },
  images: Array<{ id: string | number }>,
}

type ContentfulPage = {
  galleries: Array<ContentfulGallery>,
}

type Props = {
  location: Location,
  history: RouterHistory,
  data: {
    contentfulPage: ContentfulPage,
    site: {
      siteMetadata: {
        siteUrl: string,
      },
    },
  },
}

export default ({ data, history, location }: Props) => {
  const { galleries } = data.contentfulPage
  const { search } = location
  const categories = sortedUniq(
    sortBy(map(galleries, gallery => gallery.category.title)),
  )
  const { siteUrl } = data.site.siteMetadata
  return (
    <React.Fragment>
      <Helmet>
        <link rel="canonical" href={siteUrl} />
      </Helmet>
      <Wrapper>
        <Filter
          filters={categories}
          initialFilter={
            search.substring(1).length ? search.substring(1) : undefined
          }
          history={history}
        >
          {filter => (
            <Grid filter={filter}>
              {galleries.map(
                ({ id, slug, featuredImage, title, category, images }) => {
                  const { url, contentType } = featuredImage.file
                  return (
                    <Grid.Item
                      key={id}
                      filterTag={category.title.toLowerCase()}
                    >
                      <Link
                        to={{
                          pathname: `/gallery/${slug}`,
                          state: { filter },
                        }}
                        style={{ position: 'relative', display: 'block' }}
                      >
                        {/image/.test(contentType) ? (
                          <Img
                            sizes={featuredImage.sizes}
                            alt={featuredImage.title}
                            className={blur}
                          />
                        ) : (
                          <Gif
                            src={url}
                            contentType={contentType}
                            aspectRatio={
                              featuredImage.fields &&
                              featuredImage.fields.aspectRatio
                            }
                          />
                        )}
                        <ImageOverlay>
                          <GalleryTitle>{title}</GalleryTitle>
                          <GalleryDetails>{`${category.title} | ${
                            images.length
                          } images`}</GalleryDetails>
                        </ImageOverlay>
                      </Link>
                    </Grid.Item>
                  )
                },
              )}
            </Grid>
          )}
        </Filter>
      </Wrapper>
    </React.Fragment>
  )
}

export const pageQuery = graphql`
  query PageQuery {
    ...SiteUrl
    contentfulPage(slug: { eq: "/" }) {
      galleries {
        id
        title
        slug
        category {
          title
        }
        featuredImage {
          title
          sizes(quality: 80) {
            ...GatsbyContentfulSizes_withWebp
          }
          file {
            url
            contentType
          }
          fields {
            aspectRatio
          }
        }
        images {
          id
        }
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/pages/index.js